import { PlusIcon } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { IterableElement } from 'type-fest';

import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { ITableCallbacks, ITableHeader, Table } from '../../../../components/table/Table';
import { FloatingSelectionToolbar } from '../../../../components/table/FloatingSelectionToolbar';
import { deleteSupplierContacts } from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { formatPhoneNumber } from '../../../../utils/phonenumber';
import { Supplier } from '../Supplier';
import { useSupplier } from '../Supplier/supplierContext';
import { LinkButton } from '../../../../components/button/ButtonLink';
import { captureException } from '@sentry/react';

export type SupplierContact = IterableElement<Supplier['contacts']>;

const CONTACT_HEADERS: ITableHeader[] = [
  {
    id: 'name',
    name: 'Naam',
  },
  {
    id: 'email',
    name: 'Email',
  },
  {
    id: 'phone',
    name: 'Telefoon',
  },
  {
    id: 'function',
    name: 'Functie',
  },
  {
    id: 'language',
    name: 'Taal',
  },
  {
    id: 'notifications',
    name: 'Contact geschikt voor',
  },
];

interface IContactActionsProps {
  selections: SupplierContact[];
  refresh: () => void;
}

const ContactSelectionActions: React.FC<IContactActionsProps> = (props) => {
  const { selections, refresh } = props;

  return (
    <div>
      <ConfirmDialog
        triggerText="Verwijder"
        triggerColor="danger"
        title="Verwijder contacten"
        submitText="Verwijder"
        description={
          <div>
            <div>Ben je zeker dat je volgende contacten wilt wissen?</div>
            <ul>
              {selections.map((s) => (
                <li key={s.id}>{s.name}</li>
              ))}
            </ul>
          </div>
        }
        onSubmit={async () => {
          try {
            await deleteSupplierContacts(selections.map((s) => s.id));
            refresh();
          } catch (err) {
            captureException(err);
            toast.error('Kon contacten niet verwijderen: ' + getDisplayError(err));
          }
        }}
      />
    </div>
  );
};

export const SupplierContacts = () => {
  const { supplier, refreshData } = useSupplier();
  const [selections, setSelections] = useState<SupplierContact[]>([]);
  const [tableCallbacks, setTableCallbacks] = useState<ITableCallbacks | null>(null);

  const refresh = useCallback(() => {
    refreshData();
    tableCallbacks?.resetSelections();
  }, [refreshData, tableCallbacks?.resetSelections]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
          Nieuw contact
        </LinkButton>
      </div>

      <FloatingSelectionToolbar
        selectionCount={selections.length}
        onClear={() => tableCallbacks?.resetSelections()}
        quickActions={<ContactSelectionActions selections={selections} refresh={refresh} />}
      />

      <Table
        idKey="id"
        headers={CONTACT_HEADERS}
        data={supplier.contacts}
        onSelect={setSelections}
        registerCallbacks={setTableCallbacks}
        mapData={(data) => {
          return [
            <Link to={`${data.id}`} className="link-text">
              {data.name}
            </Link>,
            data.email,
            formatPhoneNumber(data.phone, true),
            data.function,
            data.language,
            data.shouldReceiveTransportOrders ? 'Transport Opdrachten' : '',
          ];
        }}
        isMultiSelect
      />
    </div>
  );
};
